import React from 'react';
import {
    Box, Container, Typography, Divider, Chip, Link, List, ListItem, ListItemText, Paper
} from '@mui/material';

const PRIVACY_VERSION = '2025-09-29';
const LAST_UPDATED = 'September 29, 2025';

export default function Privacy() {
  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                Privacy Policy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last updated {LAST_UPDATED} • Version {PRIVACY_VERSION}
              </Typography>
            </Box>
            <Chip label="Ski Fleet" />
          </Box>

          <Divider sx={{ my: 3 }} />

        {/* Table of contents */}
        <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary">On this page</Typography>
            <List dense>
              {[
                ['1. Introduction', 'intro'],
                ['2. Information We Collect', 'collect'],
                ['3. How We Use Your Information', 'use'],
                ['4. Data Sharing', 'sharing'],
                ['5. Data Retention', 'retention'],
                ['6. Security', 'security'],
                ['7. Children’s Privacy', 'children'],
                ['8. Your Rights', 'rights'],
                ['9. International Data Transfers', 'intl'],
                ['10. Changes to this Policy', 'changes'],
                ['11. Contact Us', 'contact'],
              ].map(([label, id]) => (
                <ListItem key={id} sx={{ py: 0 }}>
                  <ListItemText
                    primary={<Link href={`#${id}`} underline="hover">{label}</Link>}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Content */}
          <Section id="intro" title="1. Introduction">
            <P>
              Ski Fleet (“we,” “our,” or “us”) values your privacy. This Privacy Policy explains how we collect, use,
              share, and protect your personal data and performance data when you use the Ski Fleet platform (the
              “Service”). By using the Service, you consent to the practices described in this Privacy Policy.
            </P>
          </Section>

          <Section id="collect" title="2. Information We Collect">
            <Subheading>2.1 Account Information</Subheading>
            <UL items={[
              'Name, email address, and password. Users may also add height and weight.',
              'Role (athlete, coach, retailer, manufacturer, admin).'
            ]} />
            <Subheading>2.2 Performance Data</Subheading>
            <UL items={[
              'Ski fleet details (brand, model, grind, flex, etc.)',
              'Test sessions (rankings, conditions, notes)',
              'Race results (location, date, distance, conditions, ski used, performance rating)',
              'Wax logs and coach test results',
              'QR code associations'
            ]} />
            <Subheading>2.3 Technical Data</Subheading>
            <P>
                We may collect:
            </P>
            <UL items={[
              'Device/browser information',
              'Log files, IP addresses',
              'Usage statistics (pages viewed, actions taken)'
            ]} />
          </Section>

          <Section id="use" title="3. How We Use Your Information">
            <P>
                We use collected data to:
            </P>
            <UL items={[
              'Operate and improve the Service',
              'Authenticate users and secure accounts',
              'Provide analytics and reporting to athletes, coaches, and teams',
              'Train AI and machine learning models to improve ski and wax recommendations',
              'Support research and commercial insights for the ski industry'
            ]} />
          </Section>

          <Section id="sharing" title="4. Data Sharing">
            <Subheading>4.1 With Your Consent</Subheading>
            <P>We will not share personally identifiable information (name, email, etc.) with third parties without your explicit consent.</P>
            <Subheading>4.2 Aggregated & Anonymized Data</Subheading>
            <P>
            We may share, license, or sell aggregated and anonymized Performance Data with:
            </P>
            <UL items={[
              'Ski manufacturers (e.g., to analyze model/batch performance)',
              'Wax companies (e.g., to improve product recommendations)',
              'Retailers (e.g., to optimize inventory decisions)',
              'Research partners (e.g., to study ski performance trends)',
            ]} />
            <Subheading>4.3 Service Providers</Subheading>
            <P>
            We may share limited data with third-party vendors (e.g., hosting providers, analytics platforms, payment processors) 
            who help us operate the Service.
            </P>
          </Section>

          <Section id="retention" title="5. Data Retention">
          <UL items={[
              'Account data is retained as long as your account is active.',
              'Performance Data may be retained and anonymized for research and analytics even after account deletion.',
              'You may request deletion of personal data (see Section 8).',
            ]} />
          </Section>

          <Section id="security" title="6. Security">
            <P>
                We use industry-standard measures to protect your data, including:
            </P>
            <UL items={[
              'Encryption in transit (TLS/HTTPS)',
              'Secure password hashing (bcrypt)',
              'Role-based access control',
            ]} />
            <P>
                No system is 100% secure, and we cannot guarantee absolute protection.
            </P>
          </Section>

          <Section id="children" title="7. Children’s Privacy">
            <P>
              The Service is not directed to children under 13 (or the minimum legal age in your jurisdiction). We do not
              knowingly collect personal data from children.
            </P>
          </Section>

          <Section id="rights" title="8. Your Rights">
            <P>
                Depending on your location, you may have the right to:
            </P>
            <UL items={[
              'Access your personal data',
              'Correct inaccurate data',
              'Request deletion of your personal data',
              'Withdraw consent to data processing',
              'Receive a copy of your data in a portable format',
            ]} />
            <P>
                To exercise these rights, contact us at: <Link href="mailto:skifleetapp@gmail.com">skifleetapp@gmail.com</Link>
            </P>
          </Section>

          <Section id="intl" title="9. International Data Transfers">
            <P>
              If you access the Service outside Canada, your data may be processed in countries with different data
              protection laws.
            </P>
          </Section>

          <Section id="changes" title="10. Changes to this Policy">
            <P>
              We may update this Privacy Policy. If we make significant changes, we will notify you via the app or email.
              Continued use of the Service after changes constitutes acceptance.
            </P>
          </Section>

          <Section id="contact" title="11. Contact Us">
            <P>
              If you have questions, please contact us at:
              Email: <Link href="mailto:skifleetapp@gmail.com">skifleetapp@gmail.com</Link><br />
              Mailing Address: Suite #100, 1988 Olympic Way, Canmore, Alberta T1W 2T6, Canada
            </P>
          </Section>
        </Paper>
      </Container>
    </Box>
  );
}

/* Helpers */
function Section({ id, title, children }) {
  return (
    <Box id={id} sx={{ scrollMarginTop: 96, mt: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}
function Subheading({ children }) {
  return <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>{children}</Typography>;
}
function P({ children }) {
  return <Typography variant="body1" sx={{ mb: 1.5 }}>{children}</Typography>;
}
function UL({ items }) {
  return (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((t, i) => (
        <Typography key={i} component="li" variant="body1" sx={{ mb: 0.75 }}>
          {t}
        </Typography>
      ))}
    </Box>
  );
}
